/**
 * useToggleSubmission — PUT /v1/clinics/{clinic_id}/measures/{measure_id}/submission
 *
 * Powers the "Submit ✓" checkbox in the per-measure grid. Toggling changes
 * `is_selected_for_submit` for the measure and recomputes which measures
 * count toward the Quality category points + rollup totals.
 *
 * Per the locked decision in the kickoff Q&A, this is NOT optimistic — the
 * toggle waits for the mutation + dashboard invalidation to repaint. That
 * keeps the rollup row authoritative: the server always re-derives totals.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { SubmissionToggleRequest, SubmissionToggleResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface ToggleSubmissionVariables {
  measureId: number;
  /** Performance year — sent as the `performance_year` query param. */
  year: number;
  body: SubmissionToggleRequest;
}

export function useToggleSubmission(clinicId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      measureId,
      year,
      body,
    }: ToggleSubmissionVariables): Promise<SubmissionToggleResponse> => {
      const { data } = await getApiClient().put<SubmissionToggleResponse>(
        `/v1/clinics/${clinicId}/measures/${measureId}/submission`,
        body,
        { params: { performance_year: year } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.byClinic(clinicId) });
    },
  });
}
